import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { useAuth } from '../contexts/AuthContext';
import { gameService } from '../services/gameService';
import { ApiResponse, HostInfo } from '../types';

// API URL with fallback to localhost if environment variable is not set
const API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5002';

interface QuestionCollection {
  id: string;
  name: string;
  description: string;
  questionCount: number;
  difficulty: string;
  subject: string;
}

export default function CreateGame() {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [maxPlayers, setMaxPlayers] = useState(10000); // Default to 10000 players
  const [allowExternalHost, setAllowExternalHost] = useState(false);
  const [questionCollectionId, setQuestionCollectionId] = useState('');
  const [questionCount, setQuestionCount] = useState<number | ''>('');
  const [collections, setCollections] = useState<QuestionCollection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCollections, setIsLoadingCollections] = useState(true);
  const [error, setError] = useState('');
  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [showTokenWarning, setShowTokenWarning] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<QuestionCollection | null>(null);
  const [gameSpaceId, setGameSpaceId] = useState<string | null>(null);
  
  const [searchParams] = useSearchParams();
  
  const { joinGame } = useGame();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Debug logging to help diagnose button disabled issue
  useEffect(() => {
    console.log('Debug - User Type:', user?.userType);
    console.log('Debug - Host Info:', hostInfo);
    console.log('Debug - Token Balance:', hostInfo?.tokenBalance);
    console.log('Debug - Button Conditions:', {
      isLoading,
      isLoadingCollections,
      titleEmpty: !title.trim(),
      collectionEmpty: !questionCollectionId,
      playerWithNoTokens: user?.userType === 'Player' && (hostInfo?.tokenBalance || 0) <= 0
    });
  }, [user, hostInfo, isLoading, isLoadingCollections, title, questionCollectionId]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?returnUrl=/create');
      return;
    }
    
    // Get gameSpaceId from URL if present
    const gameSpaceIdParam = searchParams.get('gameSpaceId');
    if (gameSpaceIdParam) {
      setGameSpaceId(gameSpaceIdParam);
    }

    // Fetch host info for all user types
    const fetchHostInfo = async () => {
      // Always fetch host info regardless of user type
      setLoadingTokens(true);
      try {
        const response = await gameService.getHostInfo();
        if (response.success && response.data) {
          setHostInfo(response.data);
          // Show token warning if balance is 0
          setShowTokenWarning(response.data.tokenBalance <= 0);
        }
      } catch (error) {
        console.error('Failed to fetch host info:', error);
      } finally {
        setLoadingTokens(false);
      }
    };

    fetchHostInfo();

    // Load question collections
    const fetchCollections = async () => {
      try {
        const response = await fetch(`${API_URL}/api/questions/collections`);
        if (response.ok) {
          const data = await response.json();
          setCollections(Array.isArray(data) ? data : data.data || []);
        } else {
          console.error('Failed to fetch collections');
        }
      } catch (error) {
        console.error('Error fetching collections:', error);
      } finally {
        setIsLoadingCollections(false);
      }
    };

    fetchCollections();
  }, [isAuthenticated, navigate, user]);

  const handleCreateGame = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Please enter a game title');
      return;
    }

    if (!questionCollectionId) {
      setError('Please select a question collection');
      return;
    }
    
    // Validate question count if provided
    if (questionCount !== '' && selectedCollection) {
      if (questionCount <= 0) {
        setError('Question count must be greater than 0');
        return;
      }
      if (questionCount > selectedCollection.questionCount) {
        setError(`Question count cannot exceed the collection's total (${selectedCollection.questionCount})`);
        return;
      }
    }
    
    // Check if user has enough tokens
    if (hostInfo && hostInfo.tokenBalance <= 0) {
      setError('You don\'t have enough tokens to create a game. Please purchase tokens first.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await gameService.createGame({
        title,
        description: description || undefined,
        isPublic,
        maxPlayers,
        questionCollectionId,
        allowExternalHost: user?.userType === 'Member' ? Boolean(allowExternalHost) : undefined,
        questionCount: questionCount !== '' ? Number(questionCount) : undefined,
        gameSpaceId: gameSpaceId ? gameSpaceId : undefined
      });
      
      if (response.success && response.data) {
        // Automatically join the game as the host
        const gameCode = response.data.gameCode;
        const playerName = user?.displayName || 'Host';
        
        try {
          // Join the game automatically
          const joinSuccess = await joinGame(gameCode, playerName);
          
          if (joinSuccess) {
            // Navigate directly to the lobby
            navigate('/lobby');
          } else {
            // If join fails, fall back to the join game page
            navigate(`/game/${gameCode}`);
          }
        } catch (joinError) {
          console.error('Error auto-joining game:', joinError);
          // Fall back to the join game page if auto-join fails
          navigate(`/game/${gameCode}`);
        }
      } else {
        setError(response.message || 'Failed to create game');
      }
    } catch (error: any) {
      setError(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={7}>
          <div className="text-center mb-4">
            <h2 className="fw-bold text-primary">Create New Game</h2>
            <p className="text-muted">
              Set up your trivia game and invite friends to play!
            </p>
          </div>

          <Card className="border-0 shadow">
            <Card.Body className="p-4">
              {error && (
                <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              
              {/* Token Balance Information */}
              {(user?.userType === 'Member' || user?.userType === 'Player') && (
                <div className="mb-4">
                  <Card className="bg-light">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-1">Token Balance</h5>
                          <p className="text-muted mb-0">Each game session costs 1 token</p>
                        </div>
                        {loadingTokens ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Badge bg={hostInfo?.tokenBalance ? 'success' : 'danger'} className="fs-5 px-3 py-2">
                            <i className="fas fa-coins me-2"></i>
                            {hostInfo?.tokenBalance || 0} Tokens
                          </Badge>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              )}
              
              {/* Token Warning */}
              {showTokenWarning && (
                <Alert variant="warning" className="mb-4">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-exclamation-triangle me-2 fs-4"></i>
                    <div>
                      <strong>Insufficient tokens!</strong>
                      <p className="mb-0">You need at least 1 token to create a game. Visit the <Link to="/pro">Pro Management</Link> page to purchase tokens.</p>
                    </div>
                  </div>
                </Alert>
              )}

              <Form onSubmit={handleCreateGame}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Game Title*</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Give your game a catchy name"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isLoading}
                    maxLength={50}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Optional description for your game"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isLoading}
                    maxLength={200}
                  />
                  <Form.Text className="text-muted">
                    {description.length}/200 characters
                  </Form.Text>
                </Form.Group>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Game Type*</Form.Label>
                      <Form.Select 
                        value={isPublic ? 'public' : 'private'}
                        onChange={(e) => setIsPublic(e.target.value === 'public')}
                        disabled={isLoading}
                      >
                        <option value="public">Public (Anyone can join)</option>
                        <option value="private">Private (Invite only)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {user?.userType === 'Member' && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Hosting Options</Form.Label>
                    <Form.Check
                      type="checkbox"
                      id="allow-external-host"
                      label="Allow other users to host this game space"
                      checked={allowExternalHost}
                      onChange={(e) => setAllowExternalHost(e.target.checked)}
                      disabled={isLoading}
                    />
                    <Form.Text className="text-muted">
                      When enabled, other users can host games in this game space even when you're not online.
                    </Form.Text>
                  </Form.Group>
                )}

                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">Question Collection*</Form.Label>
                  {isLoadingCollections ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading collections...
                    </div>
                  ) : collections.length === 0 ? (
                    <Alert variant="warning">
                      No question collections available. Please contact an administrator.
                    </Alert>
                  ) : (
                    <div className="collection-grid">
                      {collections.map(collection => (
                        <Card 
                          key={collection.id}
                          className={`collection-card mb-3 ${questionCollectionId === collection.id ? 'border-primary' : ''}`}
                          onClick={() => {
                            setQuestionCollectionId(collection.id);
                            setSelectedCollection(collection);
                            // Reset question count when changing collections
                            setQuestionCount('');
                          }}
                        >
                          <Card.Body className="p-3">
                            <div className="d-flex align-items-start">
                              <Form.Check
                                type="radio"
                                id={`collection-${collection.id}`}
                                name="questionCollection"
                                checked={questionCollectionId === collection.id}
                                onChange={() => {
                                  setQuestionCollectionId(collection.id);
                                  setSelectedCollection(collection);
                                  // Reset question count when changing collections
                                  setQuestionCount('');
                                }}
                                className="me-2 mt-1"
                              />
                              <div>
                                <h5 className="mb-1">{collection.name}</h5>
                                <div className="mb-2 text-muted">
                                  <strong>{collection.questionCount} questions</strong> • {collection.difficulty}
                                  {collection.subject && ` • ${collection.subject}`}
                                </div>
                                {collection.description && (
                                  <p className="small mb-0">{collection.description}</p>
                                )}
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}
                </Form.Group>

                {selectedCollection && (
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Number of Questions</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder={`Enter number (max ${selectedCollection.questionCount})`}
                      value={questionCount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setQuestionCount('');
                          return;
                        }
                        
                        // Parse the input value as a number
                        const numValue = parseInt(value, 10);
                        
                        // If it's not a valid number, don't update state
                        if (isNaN(numValue)) {
                          return;
                        }
                        
                        // Update the state with the valid number
                        // The min/max attributes will handle UI validation
                        setQuestionCount(numValue);
                      }}
                      min={1}
                      max={selectedCollection.questionCount}
                    />
                    <Form.Text className="text-muted">
                      Leave empty to use all questions ({selectedCollection.questionCount}). 
                      Or specify how many questions should be randomly selected from this collection.
                    </Form.Text>
                  </Form.Group>
                )}

                <div className="d-grid gap-2">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg"
                    disabled={isLoading || isLoadingCollections || !title.trim() || !questionCollectionId || 
                      // Only check token balance for Player users, Pro users bypass this check
                      (user?.userType === 'Player' && hostInfo !== null && hostInfo.tokenBalance <= 0)}
                  >
                    {isLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating Game...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus-circle me-2"></i>
                        Create Game
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <div className="text-center mt-3">
            <Link to="/" className="text-muted text-decoration-none">
              <i className="fas fa-arrow-left me-1"></i>
              Back to Home
            </Link>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
